import type { PlanAttestation, PlanVersionSummary } from '@/lib/apiTypes'
import { usePlanAttestation } from '@/lib/hooks'
import { Tooltip } from '@/shared/components/Tooltip'

/** verified | attested-not-approved | unverified, driven by the apply-matches-review endpoint. */
export type AttestState = 'verified' | 'attested' | 'unverified'

export function attestState(a: PlanAttestation | null | undefined): AttestState {
  if (!a) return 'unverified'
  if (a.attested && a.approved && !a.stale) return 'verified'
  if (a.attested) return 'attested'
  return 'unverified'
}

export const ATTEST_STYLE: Record<AttestState, { label: string; color: string }> = {
  verified:   { label: '⛓ verified',              color: 'var(--green)' },
  attested:   { label: '⛓ attested · not approved', color: 'var(--amber)' },
  unverified: { label: '⛓ unverified',            color: 'var(--red)' },
}

/** Apply-matches-review status for a plan version (M5): resolves verified / attested-not-approved /
 *  unverified against the attestation endpoint, plus the short hash. Falls back to the ingest hash
 *  chip while the verdict loads so the row never flashes empty. */
export function AttestationStatus({ orgId, workspaceName, version }: { orgId: string; workspaceName: string; version: PlanVersionSummary }) {
  const att = usePlanAttestation(orgId, workspaceName, version.id)
  const hash = version.attestation_hash
  const shortHash = hash ? ` · ${hash.slice(0, 12)}` : ''

  if (att.loading) {
    return <span className="badge badge-neutral" style={{ fontSize: '0.6rem', color: 'var(--text3)' }}>⛓ checking{shortHash}</span>
  }
  if (!hash) return <span className="badge badge-neutral" style={{ fontSize: '0.6rem', color: 'var(--amber)' }}>no attestation</span>

  const state = attestState(att.data)
  const style = ATTEST_STYLE[state]
  const tip = att.data?.reason ?? `Attestation hash of the redacted plan: ${hash}`
  return (
    <Tooltip text={`${tip} (hash ${hash})`} placement="top">
      <span className="badge badge-neutral" style={{ fontSize: '0.6rem', color: style.color, borderColor: style.color }}>
        {style.label}{shortHash}
      </span>
    </Tooltip>
  )
}
